import React, { useMemo } from 'react';
import { Box, Chip, Divider, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { TestAccordionSection } from '@features/calibration/components';
import { getStatusBadgeProps } from '@features/calibration/components/shared';
import {
  getCalibrationStatus,
  getStatusConfig
} from '@features/calibration/utils';
import type { DatasheetGroup } from '@features/calibration/hooks/useGroupedDatasheet';
import type {
  CommitMeasurementHandler,
  RunnerLiveState,
  RunnerSaveStatus,
  RunnerVariant
} from '../types';
import RunnerToolbar from './RunnerToolbar';
import RunnerTable from './RunnerTable';
import RunnerMobileList from './RunnerMobileList';

type RunnerSectionProps = {
  group: DatasheetGroup;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  readOnly?: boolean;
  robotStatus?: React.ReactNode;
  saveStatus?: RunnerSaveStatus;
  liveState?: RunnerLiveState;
  runId?: string;
  variant?: RunnerVariant;
  onCommitMeasurement?: CommitMeasurementHandler;
};

const HeaderSegment: React.FC<{ label: string; value: string }> = ({
  label,
  value
}) => (
  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'baseline' }}>
    <Typography variant="caption" color="text.secondary">
      {label}:
    </Typography>
    <Typography variant="body2" fontWeight={600}>
      {value}
    </Typography>
  </Box>
);

const SideChip: React.FC<{
  label: string;
  status: DatasheetGroup['asFoundStatus'];
  isDarkMode: boolean;
}> = ({ label, status, isDarkMode }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 0.5,
      alignItems: 'center'
    }}
  >
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ fontSize: '0.6875rem' }}
    >
      {label}:
    </Typography>
    <Chip size="small" {...getStatusBadgeProps(status, isDarkMode)} />
  </Box>
);

const RunnerSection: React.FC<RunnerSectionProps> = ({
  group,
  expanded,
  onExpandedChange,
  readOnly = false,
  robotStatus,
  saveStatus,
  liveState,
  runId,
  variant = 'desktop',
  onCommitMeasurement
}) => {
  const { t } = useTranslation('calibration');
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const touchedPoints = useMemo(
    () =>
      group.points.filter(p => p.asFound !== null || p.asLeft !== null).length,
    [group.points]
  );

  const overallStatus = useMemo(
    () =>
      getCalibrationStatus(
        {
          completedPoints: touchedPoints,
          totalPoints: group.total,
          asFoundPassed: group.asFoundStatus === 'pass',
          asLeftPassed: group.asLeftStatus === 'pass',
          asFoundStatus: group.asFoundStatus,
          asLeftStatus: group.asLeftStatus
        },
        key => t(key),
        isDarkMode
      ),
    [group, touchedPoints, isDarkMode, t]
  );

  const statusConfig = useMemo(
    () => getStatusConfig(isDarkMode)[group.overallStatus],
    [isDarkMode, group.overallStatus]
  );

  const statusIndicator = (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mr: 2 }}>
      <HeaderSegment
        label={t('runner.function')}
        value={group.functionSegment}
      />
      {group.unit && (
        <>
          <Divider orientation="vertical" flexItem />
          <HeaderSegment label={t('runner.unit')} value={group.unit} />
        </>
      )}
      <Divider orientation="vertical" flexItem />
      <SideChip
        label={t('testGrid.asFound')}
        status={group.asFoundStatus}
        isDarkMode={isDarkMode}
      />
      <SideChip
        label={t('testGrid.asLeft')}
        status={group.asLeftStatus}
        isDarkMode={isDarkMode}
      />
      <Divider orientation="vertical" flexItem />
      <Typography variant="body2" fontWeight={600}>
        {touchedPoints}/{group.total}
      </Typography>
      <Chip
        label={overallStatus.label}
        color={overallStatus.color}
        size="small"
        sx={{
          ...(isDarkMode && {
            backgroundColor: overallStatus.bgColor,
            color: overallStatus.textColor
          })
        }}
      />
    </Box>
  );

  return (
    <TestAccordionSection
      id={`runner-section-${group.groupKey}`}
      title={group.groupLabel}
      status={statusConfig}
      expanded={expanded}
      onExpandedChange={onExpandedChange}
      isDarkMode={isDarkMode}
      statusIndicator={statusIndicator}
    >
      <RunnerToolbar
        readOnly={readOnly}
        robotStatus={robotStatus}
        saveStatus={saveStatus}
      />
      {variant === 'desktop' ? (
        <RunnerTable
          group={group}
          readOnly={readOnly}
          liveState={liveState}
          runId={runId}
          onCommitMeasurement={onCommitMeasurement}
        />
      ) : (
        <RunnerMobileList
          group={group}
          readOnly={readOnly}
          liveState={liveState}
          runId={runId}
          sideBySide={variant === 'tablet'}
          onCommitMeasurement={onCommitMeasurement}
        />
      )}
    </TestAccordionSection>
  );
};

export default RunnerSection;
